<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.body>
      <ValidationObserver ref="referenceValidation">
        <div class="container col-lg-6 col-sm-12 offers-form">
          <h1 class="mt-5 mb-3">{{ title }}</h1>
          <div class="d-flex">
            <ValidationProvider
              class="col-6 pr-4"
              v-slot="{ errors }"
              name="firstName"
              rules="required|max:255"
            >
              <v-text-field
                v-model="referee.firstName"
                :error-messages="getErrorMessages(errors)"
                label="First Name"
              />
            </ValidationProvider>
            <ValidationProvider
              class="col-6"
              v-slot="{ errors }"
              name="lastName"
              rules="required|max:255"
            >
              <v-text-field
                :error-messages="getErrorMessages(errors)"
                v-model="referee.lastName"
                label="Last Name"
              />
            </ValidationProvider>
          </div>
          <ValidationProvider
            v-slot="{ errors }"
            name="jobTitle"
            rules="required|max:255"
          >
            <v-text-field
              v-model="referee.jobTitle"
              :error-messages="getErrorMessages(errors)"
              label="Job Title"
            />
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            name="company"
            rules="required|max:255"
          >
            <v-text-field
              v-model="referee.company"
              :error-messages="getErrorMessages(errors)"
              label="Company"
            />
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            name="phoneNumber"
            rules="required|max:255"
          >
            <v-text-field
              v-model="referee.phoneNumber"
              :error-messages="getErrorMessages(errors)"
              label="Phone Number"
            />
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            name="email"
            rules="required|email|max:255"
          >
            <v-text-field
              :error-messages="getErrorMessages(errors)"
              v-model="referee.emailAddress"
              label="Work Email"
            />
          </ValidationProvider>
          <label>Type of Reference</label>
          <RadioButton
            @select="handleRadioChange"
            :selected-value="referenceTypeName"
            :options="referenceTypes"
            showGroupInRow
          />
          <div class="mx-4 sub-content" v-if="requireRoleToBeReference">
            <ValidationProvider
              v-slot="{ errors }"
              name="jobTitle"
              rules="required"
            >
              <Select
                v-model="experienceId"
                label="Role to be referenced"
                :loading="isFetchingUserExperience"
                :items="currentUserExperience"
                :error-messages="getErrorMessages(errors)"
                item-text="jobTitle"
                item-value="id"
                autocomplete
              />
            </ValidationProvider>
          </div>
        </div>
      </ValidationObserver>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton
        @click.native="updateReference"
        :loading="saving"
        v-if="isUpdate"
      >
        Save Changes
      </PrimaryButton>
      <PrimaryButton @click.native="addReference" :loading="saving" v-else>
        Add Reference
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import RadioButton from "@/components/common/RadioButton";
import { REFERENCE_TYPES } from "@/constants/offers";
import Select from "@/components/common/Select";
import { createNamespacedHelpers } from "vuex";
import { EXPERIENCE_NAMESPACE } from "@/store/modules/users/experience";
import { FETCH_USER_EXPERIENCE } from "@/store/modules/users/experience/actions";
import {
  ADD_NEW_REFERENCE,
  UPDATE_USER_REFERENCE
} from "@/store/modules/references/actions";
import { REFERENCES_NAMESPACE } from "@/store/modules/references";
import { REFEREE_NAMESPACE } from "@/store/modules/referee";
import {
  CREATE_REFEREE,
  UPDATE_REFEREE
} from "@/store/modules/referee/actions";
import { get } from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { getReferee } from "@/utils/offers";
import { scrollToError } from "@/utils/helpers";

const {
  mapActions: experienceMapActions,
  mapState: experienceMapState
} = createNamespacedHelpers(EXPERIENCE_NAMESPACE);
const { mapActions } = createNamespacedHelpers(REFERENCES_NAMESPACE);
const { mapActions: refereeMapActions } = createNamespacedHelpers(
  REFEREE_NAMESPACE
);

export default {
  props: {
    isOpen: Boolean,
    isUpdate: Boolean,
    candidate: {
      type: Object,
      required: true
    },
    reference: Object
  },
  components: {
    DialogFull,
    PrimaryButton,
    RadioButton,
    Select,
    ValidationObserver,
    ValidationProvider
  },
  async created() {
    await this.fetchExperience(this.userId);
  },
  data() {
    return {
      referenceTypes: REFERENCE_TYPES,
      experienceId: get(this.reference, "experience.id") || null,
      referenceTypeName:
        get(this.reference, "referenceTypeName") || "Professional Reference",
      referee: getReferee(get(this.reference, "referee")),
      saving: false,
      showValidationErrors: false
    };
  },
  computed: {
    ...experienceMapState([
      "isFetchingUserExperience",
      "currentUserExperience"
    ]),
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: "New Reference"
      };
    },
    userId() {
      return this.candidate.id;
    },
    title() {
      return this.isUpdate ? "Edit Reference" : "New Reference";
    },
    currentOfferId() {
      return this.$route.params.id;
    },
    requireRoleToBeReference() {
      return this.referenceTypeName === "Professional Reference";
    },
    getExperience() {
      return this.experienceId && { id: this.experienceId };
    }
  },
  methods: {
    ...experienceMapActions({
      fetchExperience: FETCH_USER_EXPERIENCE
    }),
    ...refereeMapActions({
      createReferee: CREATE_REFEREE,
      updateReferee: UPDATE_REFEREE
    }),
    ...mapActions({
      createUserReference: ADD_NEW_REFERENCE,
      updateUserReference: UPDATE_USER_REFERENCE
    }),
    async addReference() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.referenceValidation.validate();
      if (isValid) {
        this.saving = true;
        const { id } = await this.createReferee(this.referee);
        await this.createUserReference({
          referee_user_id: id,
          referenceTypeName: this.referenceTypeName,
          user: { id: this.userId },
          ...(this.requireRoleToBeReference && {
            experience: this.getExperience
          }),
          status: "draft",
          offer: {
            id: this.currentOfferId
          }
        });
        this.saving = false;
        this.showValidationErrors = false;
        this.close();
      } else {
        scrollToError();
      }
    },
    async updateReference() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.referenceValidation.validate();
      if (isValid) {
        this.saving = true;
        this.referee.id
          ? await this.updateReferee(this.referee)
          : (this.referee = await this.createReferee(this.referee));
        await this.updateUserReference({
          id: this.reference.id,
          referee_user_id: this.referee.id,
          referenceTypeName: this.referenceTypeName,
          user: { id: this.userId },
          experience: this.getExperience,
          status: this.reference.status
        });
        this.$emit("updated");
        this.saving = false;
        this.showValidationErrors = false;
        this.close();
      } else {
        scrollToError();
      }
    },
    close() {
      this.$emit("close");
    },
    handleRadioChange(selectedValue) {
      this.referenceTypeName = selectedValue;
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    }
  }
};
</script>

<style lang="scss" scope>
.offers-form {
  .v-avatar {
    background-color: #e3f2ff !important;
    border-radius: 4px;
  }
  .offers-label {
    label {
      left: -36px !important;
    }
  }
  span {
    padding: 0 !important;
  }
}
</style>
